import React, { useState, useEffect, useCallback, useContext } from "react";
import AnimationPlayer from "../../../Common/Animation-Player/AnimationPlayer";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import NojobsAnimation from "../../../Assets/Json/nodata-found-robot-animation.json";
import { formatInterviewerName } from "../../../Common/Common-Function/CommonFunctions.js";
import Accordion from "../../../Common/Accordion/Accordion";
import { getStageColor } from "../../../Common/constants/stageColors.js";
import SidePopup from "../../../Common/SidePopup/SidePopup.js";
import CandidateDetailsContainer from "../../../Common/Candidate-Details-Container/CandidateDetailsContainer.js";
import {
  VYLABS_BASE_URL,
  GET_CANDIDATE_BY_ID,
  GET_VENDOR_DETAILS_BY_ID,
  UPDATE_CANDIDATE_DETAILS,
  VENDORS_SEARCH,
} from "../../../helper/url_helper.js";
import { get, post } from "../../../helper/Api_helper.js";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar.js";
import { DynamicContext } from "../../../Context/DynamicContext.js";
import { fetchCallStages } from "../../../Common/DynamicCallStage/DynamicCallStageMapping.js";

function AssignedCandidatesList({
  applications,
  recruiterNames,
  stageCounts,
  isSelectMode,
  selectedCandidates,
  onSelectCandidate,
}) {
  const { setIsFromJobRecommendedCandidates } = useContext(DynamicContext);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isCandidateDetailsOpen, setIsCandidateDetailsOpen] = useState(false);
  const [candidateDetailsLoading, setCandidateDetailsLoading] = useState(false);
  const [candidateDetailsError, setCandidateDetailsError] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [isEditingCandidate, setIsEditingCandidate] = useState(false);
  const [originalCandidateData, setOriginalCandidateData] = useState(null);
  const [editedCandidateData, setEditedCandidateData] = useState(null);
  const [vendorList, setVendorList] = useState([]);
  const [vendorOptionLoading, setVendorOptionLoading] = useState(false);
  const [assignedVendor, setAssignedVendor] = useState(null);
  const [callStageMap, setCallStageMap] = useState({});

  useEffect(() => {
    const loadCallStages = async () => {
      const stages = await fetchCallStages();
      setCallStageMap(stages);
    };

    loadCallStages();
  }, []);

  const calculateExperience = (candidate) => {
    if (!candidate?.experience) return "N/A";
    const years = Math.floor(candidate.experience / 12);
    return years > 0 ? years : "N/A";
  };

  const fetchCandidateById = useCallback(
    async (id) => {
      try {
        const res = await get(
          VYLABS_BASE_URL,
          `${GET_CANDIDATE_BY_ID}?id=${id}`,
        );
        const candidate = res?.candidate;
        const candidatefiles = res?.files;
        const parsedResumeData = candidate?.parsed_resume
          ? JSON.parse(candidate.parsed_resume)
          : null;
        const presignedUrl =
          candidatefiles && candidatefiles.length > 0
            ? candidatefiles[0].presigned_url
            : null;
        const updatedCandidate = {
          ...candidate,
          secondaryEmail: candidate.secondary_email,
          secondaryPhone: candidate.secondary_phone,
          vendorID: candidate.vendor_id,
          lineAddress1: candidate.line_address_1,
          lineAddress2: candidate.line_address_2,
          preferredSalary: candidate.preferred_salary,
          zipCode: candidate.zip_code,
          parsedResumeData,
          files: candidatefiles || null,
          presignedUrl,
          isFileBased: false,
          avatar: res?.profile_img_url,
        };
        if (updatedCandidate.vendorID) {
          try {
            const vendorResponse = await get(
              VYLABS_BASE_URL,
              `${GET_VENDOR_DETAILS_BY_ID}?id=${updatedCandidate.vendorID}`,
            );
            setAssignedVendor(vendorResponse);
            updatedCandidate.vendorDetails = vendorResponse?.vendor || null;
          } catch (err) {
            console.error("Error fetching vendor details:", err);
            updatedCandidate.vendorDetails = null;
            setAssignedVendor(null);
          }
        } else {
          updatedCandidate.vendorDetails = null;
          setAssignedVendor(null);
        }
        setSelectedCandidate(updatedCandidate);
        return updatedCandidate;
      } catch (error) {
        console.error("Error fetching candidate:", error);
        Toast("error", error.response.data || "Failed to fetch candidate details");
      }
    },
    [setSelectedCandidate, setAssignedVendor],
  );

  const handleCardView = async (candidateId) => {
    setActiveTab(0);
    setCandidateDetailsLoading(true);
    setCandidateDetailsError("");
    const details = await fetchCandidateById(candidateId);
    if (!details) {
      setCandidateDetailsError(
        "Failed to load candidate details. Please try again later.",
      );
      setSelectedCandidate(null);
      setCandidateDetailsLoading(false);
      return;
    } else {
      setSelectedCandidate(details);
    }
    setIsFromJobRecommendedCandidates(true);
    setIsCandidateDetailsOpen(true);
    setCandidateDetailsLoading(false);
  };

  const handleCloseCandidateDetails = () => {
    setIsCandidateDetailsOpen(false);
    setSelectedCandidate(null);
    setIsFromJobRecommendedCandidates(false);
    resetCandidateEdit();
  };

  const handleTabChange = (index) => {
    setActiveTab(index);
  };

  const resetCandidateEdit = () => {
    if (originalCandidateData) {
      setSelectedCandidate(originalCandidateData);
    }
    setIsEditingCandidate(false);
    setOriginalCandidateData(null);
    setEditedCandidateData(null);
  };

  const handleEditCandidate = () => {
    const getValidExperience = (value) => {
      const num = Number(value);

      if (isNaN(num)) return "";
      if (num < 0) return 0;
      return num;
    };

    let experienceValue = "";
    const directExp = getValidExperience(
      selectedCandidate?.years_of_experience,
    );

    const parsedExp = getValidExperience(
      selectedCandidate?.parsedResumeData?.BasicInfo?.YearsOfExperience,
    );

    if (directExp !== "") {
      experienceValue = directExp;
    } else if (parsedExp !== "") {
      experienceValue = parsedExp;
    }
    const candidateWithFormattedSkills = {
      ...selectedCandidate,
      name: selectedCandidate.name || selectedCandidate.BasicInfo?.Name || "",
      email:
        selectedCandidate.email || selectedCandidate.BasicInfo?.Email || "",
      phone:
        selectedCandidate.phone ||
        selectedCandidate.BasicInfo?.PhoneNumber ||
        "",
      skills: Array.isArray(selectedCandidate.skills)
        ? selectedCandidate.skills
        : selectedCandidate.parsedResumeData?.Skills || [],
      designation:
        selectedCandidate.designation?.trim() ||
        selectedCandidate.parsedResumeData?.CareerHistory?.JobDetails?.[0]
          ?.JobTitle ||
        "",
      experience: experienceValue,
      lineAddress1:
        selectedCandidate.lineAddress1 ||
        selectedCandidate.BasicInfo?.LineAddress1 ||
        "",
      lineAddress2:
        selectedCandidate.lineAddress2 ||
        selectedCandidate.BasicInfo?.LineAddress2 ||
        "",
      zipCode:
        selectedCandidate.zipCode || selectedCandidate.BasicInfo?.ZipCode || "",
      preferredSalary:
        selectedCandidate.preferredSalary ||
        selectedCandidate.BasicInfo?.PreferredSalary ||
        "",
    };
    setOriginalCandidateData({ ...selectedCandidate });
    setEditedCandidateData(candidateWithFormattedSkills);
    setIsEditingCandidate(true);
  };

  const handleCandidateChange = (data) => {
    setEditedCandidateData((prev) => ({ ...prev, ...data }));
  };

  const saveCandidateChanges = async () => {
    if (!editedCandidateData) {
      Toast("error", "No changes to save");
      return;
    }

    try {
      const candidateData = { id: selectedCandidate.id };
      Object.keys(editedCandidateData).forEach((key) => {
        if (editedCandidateData[key] !== selectedCandidate[key]) {
          candidateData[key] = editedCandidateData[key];
        }
      });

      if (Object.keys(candidateData).length === 1) {
        Toast("info", "No changes were made.");
        setIsEditingCandidate(false);
        return;
      }

      Object.keys(candidateData).forEach((key) => {
        if (key === "preferredSalary") {
          candidateData[key] = Number(candidateData[key]);
        }
      });

      if (candidateData.experience !== undefined) {
        candidateData.yearsOfExperience = Number(candidateData.experience);
        delete candidateData.experience;
      }

      const response = await post(
        VYLABS_BASE_URL,
        UPDATE_CANDIDATE_DETAILS,
        candidateData,
      );

      if (response) {
        const updatedCandidate = { ...selectedCandidate, ...candidateData };
        setSelectedCandidate(updatedCandidate);
        setIsEditingCandidate(false);
        setOriginalCandidateData(null);
        setEditedCandidateData(null);
        Toast("success", "Candidate updated successfully");
      }
    } catch (error) {
      console.error("Error updating candidate:", error);
      Toast("error", "Failed to update candidate");
    }
  };

  const fetchVendors = async (query) => {
    try {
      setVendorOptionLoading(true);
      const res = await get(
        VYLABS_BASE_URL,
        `${VENDORS_SEARCH}?vendor_name=${encodeURIComponent(query)}`,
      );
      setVendorList(
        (res.vendors || [])
          .filter((v) => v.id && v.name)
          .map((v) => ({
            label: v.contact_person
              ? `${v.name} - ${v.contact_person}`
              : v.name,
            value: v.id,
          })),
      );
    } catch {
      setVendorList([]);
    } finally {
      setVendorOptionLoading(false);
    }
  };

  const handleVendorInputChange = (_, value) => {
    if (value && value.length >= 3) fetchVendors(value);
    else setVendorList([]);
  };

  if (!applications || applications.length === 0) {
    return (
      <div
        style={{
          // display: "flex",
          // justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#6665ea",
            marginBottom: "-18%",
            marginTop: "40px",
          }}
        >
          No candidates assigned yet.
        </h2>
        <AnimationPlayer animationSrc={NojobsAnimation} playerWidth="300px" />
      </div>
    );
  }

  const getCandidateId = (application) =>
    application?.candidate_id ??
    application?.candidate?.id ??
    application?.candidate?.candidate_id;

  const isStageFullySelected = (apps) => {
    if (!isSelectMode || !apps?.length) return false;

    return apps.every((application) => {
      const candidateId = getCandidateId(application);
      return selectedCandidates.some(
        (c) => String(c?.id ?? c?.candidate_id) === String(candidateId),
      );
    });
  };

  const handleStageSelectAll = (apps) => {
    if (!isSelectMode || !apps?.length) return;

    const allSelected = isStageFullySelected(apps);

    apps.forEach((application) => {
      const candidateId = getCandidateId(application);

      if (allSelected) {
        const selected = selectedCandidates.find(
          (c) => String(c?.id ?? c?.candidate_id) === String(candidateId),
        );

        if (selected) {
          onSelectCandidate({ ...selected, id: candidateId });
        }
      } else {
        const alreadySelected = selectedCandidates.some(
          (c) => String(c?.id ?? c?.candidate_id) === String(candidateId),
        );

        if (!alreadySelected) {
          onSelectCandidate({
            ...application.candidate,
            id: candidateId,
          });
        }
      }
    });
  };

  const renderCandidateCard = (application) => {
    const { candidate } = application;
    const experience = calculateExperience(candidate);
    const recruiterName = formatInterviewerName(
      recruiterNames?.[application.assigned_recruiter_id],
    );
    const stageName =
      callStageMap[application.stage] || application.stage || "New Profile";

    const isInterestedStage = application.stage !== "";
    const showCheckbox = isSelectMode && isInterestedStage;
    const candidateId =
      application.candidate_id ?? candidate?.id ?? candidate?.candidate_id;
    const isSelected =
      showCheckbox &&
      selectedCandidates.some(
        (c) => String(c?.id ?? c?.candidate_id) === String(candidateId),
      );

    const handleItemClick = () => {
      if (showCheckbox) {
        onSelectCandidate({ ...candidate, id: candidateId });
      } else {
        handleCardView(application.candidate_id);
      }
    };

    return (
      <div
        key={application.id}
        className="assigned-candidate-list-item"
        onClick={handleItemClick}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "18px",
          border: isSelected
            ? "2px solid #6860ee"
            : "1px solid var(--border__color)",
          borderRadius: "12px",
          backgroundColor: isSelected
            ? "rgba(104, 96, 238, 0.1)"
            : "var(--body__background)",
          cursor: "pointer",
          gap: "20px",
          marginBottom: "12px",
        }}
      >
        {showCheckbox && (
          <div>
            {isSelected ? (
              <CheckBoxIcon style={{ color: "#6860ee" }} />
            ) : (
              <CheckBoxOutlineBlankIcon
                style={{ color: "var(--header__color)" }}
              />
            )}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "38px",
              height: "38px",
              borderRadius: "50%",
              background: "#d1d5db",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "600",
              color: "#111",
            }}
          >
            {candidate?.name?.[0]}
          </div>
          <span style={{ fontSize: "15px", fontWeight: "600" }}>
            {candidate?.name || "N/A"}
          </span>
        </div>

        <div style={{ flex: "1 1 160px" }}>
          <span
            style={{
              backgroundColor: "#6860ee",
              padding: "4px 12px",
              borderRadius: "20px",
              fontSize: "13px",
              whiteSpace: "nowrap",
              color: "#fff",
            }}
          >
            {stageName}
          </span>
        </div>

        <div
          style={{ flex: "1 1 120px", fontSize: "14px", textAlign: "center" }}
        >
          {experience !== "N/A" ? `${experience} Yrs` : "N/A"}
        </div>

        <div
          style={{ flex: "1 1 120px", fontSize: "14px", textAlign: "center" }}
        >
          {candidate?.city || "N/A"}
        </div>

        <div style={{ flex: "1 1 auto", fontSize: "14px", textAlign: "right" }}>
          <span style={{ display: "block", marginBottom: "5px" }}>
            Recruiter
          </span>
          {recruiterName}
        </div>
      </div>
    );
  };

  const groupedByStage = applications.reduce((acc, app) => {
    const stage = app.stage || "N/A";
    if (!acc[stage]) {
      acc[stage] = [];
    }
    acc[stage].push(app);
    return acc;
  }, {});

  return (
    <>
      {Object.entries(groupedByStage).map(([stage, apps], index) => (
        <div key={stage} style={{ marginTop: "15px" }}>
          <Accordion
            title={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {isSelectMode && (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleStageSelectAll(apps);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: apps.length ? "pointer" : "default",
                      flexShrink: 0,
                    }}
                    title={
                      isStageFullySelected(apps)
                        ? `Deselect all candidates in ${stage}`
                        : `Select all candidates in ${stage}`
                    }
                  >
                    {isStageFullySelected(apps) ? (
                      <CheckBoxIcon style={{ color: "#6860ee" }} />
                    ) : (
                      <CheckBoxOutlineBlankIcon
                        style={{ color: "var(--header__color)" }}
                      />
                    )}
                  </div>
                )}
                <span>{callStageMap[stage] || stage}</span>
              </div>
            }
            count={stageCounts ? stageCounts[stage] || 0 : apps.length}
            countBackgroundColor="#6860ee"
            defaultOpen={index === 0}
            accordionHeaderStyle={{
              borderRadius: "8px",
              border: "1px solid var(--border__color)",
            }}
            titleColor="var(--header__color)"
          >
            <div style={{ padding: "2px" }}>
              {apps.map((application) => renderCandidateCard(application))}
            </div>
          </Accordion>
        </div>
      ))}
      {isCandidateDetailsOpen && (
        <SidePopup
          isOpen={isCandidateDetailsOpen}
          onClose={handleCloseCandidateDetails}
          title="Candidate Details"
          sidePopupWidth="85%"
        >
          {candidateDetailsLoading ? (
            <div style={{ padding: 40, textAlign: "center" }}>Loading...</div>
          ) : candidateDetailsError ? (
            <div style={{ padding: 40, textAlign: "center", color: "#d32f2f" }}>
              {candidateDetailsError}
            </div>
          ) : selectedCandidate ? (
            <div className="candidate-details-wrapper">
              <CandidateDetailsContainer
                candidate={selectedCandidate}
                activeTab={activeTab}
                handleTabChange={handleTabChange}
                isEditingCandidate={isEditingCandidate}
                handleEditCandidate={handleEditCandidate}
                saveCandidateChanges={saveCandidateChanges}
                resetCandidateEdit={resetCandidateEdit}
                handleCandidateChange={handleCandidateChange}
                editedCandidateData={editedCandidateData}
                vendorList={vendorList}
                vendorOptionLoading={vendorOptionLoading}
                handleVendorInputChange={handleVendorInputChange}
                assignedVendor={assignedVendor}
                showCandidateInterviewTab={false}
                showCandidateRecommendedJobsTab={false}
              />
            </div>
          ) : null}
        </SidePopup>
      )}
    </>
  );
}

export default AssignedCandidatesList;
